/**
 * usePlanGate — React hook combining plan tier + feature matrix.
 *
 * Provides `canAccess(feature)` to check if the merchant's current plan
 * unlocks a specific feature, and `isWithinQuota()` to check resource limits.
 *
 * Composes naturally with `usePermissions` from the RBAC layer:
 *   const { can } = usePermissions();   // Role says you CAN
 *   const { canAccess } = usePlanGate(); // Plan says you MAY
 *
 * Usage:
 *   const { canAccess, canCreate, isWithinQuota, isFrozen } = usePlanGate();
 *
 *   // Feature check
 *   if (canAccess('staff_management')) { ... }
 *
 *   // Quota check
 *   if (isWithinQuota('users', currentStaffCount)) { ... }
 *
 *   // Create guard — not frozen AND feature allowed
 *   if (canCreate('purchasing')) { ... }
 */

import { useMemo } from 'react';
import { useMerchantPlan, type MerchantPlanState } from './useMerchantPlan';
import {
  type PlanFeature,
  type PlanTier,
  planHasFeature,
  isAtLeastTier,
  getMinTierForFeature,
  FEATURE_DISPLAY_NAMES,
} from './features';

export type QuotaType = 'stores' | 'users' | 'devices';

export interface PlanGateContext {
  /** The full merchant plan state */
  plan: MerchantPlanState;
  /** Check if a plan-gated feature is available on the current tier */
  canAccess: (feature: PlanFeature) => boolean;
  /** Check if a resource count is within the plan's quota limit */
  isWithinQuota: (type: QuotaType, currentCount: number) => boolean;
  /** Combined check: feature is accessible AND account is not frozen */
  canCreate: (feature: PlanFeature) => boolean;
  /** Check if the current tier is at least the specified tier */
  isAtLeast: (tier: PlanTier) => boolean;
  /** True if account is frozen (read-only mode) */
  isFrozen: boolean;
  /** True if account is in grace period */
  isGracePeriod: boolean;
  /** Get the minimum tier required for a feature (for upgrade prompts) */
  requiredTierFor: (feature: PlanFeature) => PlanTier | undefined;
  /** Get the display name for a feature (for UI) */
  featureName: (feature: PlanFeature) => string;
}

export function usePlanGate(): PlanGateContext {
  const plan = useMerchantPlan();

  return useMemo(() => {
    const { planTier, isFrozen, isGracePeriod, maxStores, maxUsers, maxDevices } = plan;

    const quotaLimits: Record<QuotaType, number> = {
      stores: maxStores,
      users: maxUsers,
      devices: maxDevices,
    };

    return {
      plan,
      canAccess: (feature: PlanFeature) => planHasFeature(planTier, feature),
      isWithinQuota: (type: QuotaType, currentCount: number) => {
        const limit = quotaLimits[type];
        // 0 or negative limit = unlimited (ENTERPRISE convention)
        if (limit <= 0) return true;
        return currentCount < limit;
      },
      canCreate: (feature: PlanFeature) => !isFrozen && planHasFeature(planTier, feature),
      isAtLeast: (tier: PlanTier) => isAtLeastTier(planTier, tier),
      isFrozen,
      isGracePeriod,
      requiredTierFor: getMinTierForFeature,
      featureName: (feature: PlanFeature) => FEATURE_DISPLAY_NAMES[feature] ?? feature,
    };
  }, [plan]);
}
